import { getLogger } from "@expert/logging";
import { createContext, useContext, useEffect } from "react";
import type reactUseWebSocket from "react-use-websocket";
import { ReadyState } from "react-use-websocket";
import type { ExtraHeartbeatProps, SendMessageToTenantType } from "../hooks/useWebsocket";
import { useWebsocketInternal } from "../hooks/useWebsocket";

type SendJsonMessage = ReturnType<typeof reactUseWebSocket>["sendJsonMessage"];

export interface ExpertWorkspaceWebSocket {
    sendWebSocketMessage: SendJsonMessage;
    sendMessageToTenant: SendMessageToTenantType;
    failedToLoad: boolean;
    loading: boolean;
    reconnect: () => void;
    isConnected: boolean;
    upsertExtraHeartbeatProps: (extraHeartbeatProps: ExtraHeartbeatProps) => void;
}

const WebSocketContext = createContext<ExpertWorkspaceWebSocket>({
    sendWebSocketMessage: () => {},
    sendMessageToTenant: () => {},
    failedToLoad: false,
    loading: true,
    reconnect: () => {},
    isConnected: false,
    upsertExtraHeartbeatProps: () => {},
});

interface WebSocketProviderProps {
    token: string;
    identity: string;
    webSocketUrl?: string;
    isWebsocketEnabled: boolean;
    onDisconnect?: () => void;
    children?: React.ReactNode;
}

const logger = getLogger({ module: "ExpertWorkspaceWebSocketProvider" });

export function ExpertWorkspaceWebSocketProvider({
    token,
    identity,
    webSocketUrl,
    isWebsocketEnabled,
    onDisconnect,
    children,
}: WebSocketProviderProps) {
    const {
        sendWebSocketMessage,
        sendMessageToTenant,
        loading,
        readyState,
        failedToLoad,
        reconnect,
        upsertExtraHeartbeatProps,
    } = useWebsocketInternal({
        token,
        identity,
        onDisconnect,
        isWebsocketEnabled,
        environmentType: import.meta.env.VITE_ENV_TYPE as "development" | "production",
        webSocketUrl,
    });
    // TODO: Decide how do we want to handle WS reconnect/disconnect
    // if (loading) return <GlobalLoader msg="Creating websocket connection..." />;
    useEffect(() => {
        logger.trace(`==== WEB SOCKET STATE IS ${readyState}, LOADING: ${loading} ====`);
    }, [loading, readyState]);

    const isConnected = readyState === ReadyState.OPEN || readyState === ReadyState.CLOSING;

    return (
        <WebSocketContext.Provider
            value={{
                sendWebSocketMessage,
                sendMessageToTenant,
                failedToLoad,
                loading,
                reconnect,
                isConnected,
                upsertExtraHeartbeatProps,
            }}
        >
            {children}
        </WebSocketContext.Provider>
    );
}

export function useExpertWorkspaceWebSocket() {
    const context = useContext(WebSocketContext);
    return context;
}
